import { useIonRouter } from '@ionic/react'
import clsx from 'clsx'
import { bookmarks, home, notifications, person, search } from 'ionicons/icons'
import { useEffect, useLayoutEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useContextSelector } from 'use-context-selector'

import type { ContactRequestFragment, TalentTransferRequest } from '../../lib/apollo/types'
import useTabContext from '../../lib/hooks/useTabContext'
import { AppContext } from '../AppState'
import TabButton from '../TabButton'

import './style.scss'
import {Keyboard} from '@capacitor/keyboard'
import {Capacitor} from '@capacitor/core'

export const showTabBarRoutes = ['/home', '/profile', '/inbox', '/playlists', '/comparison', '/marketplace', '/settings']

/**
 * In einer Unterhaltung sitzt die Nachrichteneingabe am unteren Rand. Die
 * Tab-Leiste liegt als absolutes Element genau darueber und verdeckt sie —
 * zurueck geht es dort ueber den Pfeil in der Kopfzeile.
 */
const hideTabBarRoutes = ['/inbox/conversations/']

export const showsTabBar = (pathname: string): boolean =>
    showTabBarRoutes.some((route: string) => pathname.startsWith(route))
    && !hideTabBarRoutes.some((route: string) => pathname.startsWith(route))

export const getTalentTransferBadge = (talentTransferRequests: TalentTransferRequest[] | undefined) : string | number | undefined => {
    if (!talentTransferRequests || talentTransferRequests.length === 0) return undefined

    return talentTransferRequests.length <= 9 ? talentTransferRequests.length : '9+'
}

/**
 * Die Zahl am Postfach. Sie zaehlt bewusst nur, worauf jemand eine Antwort
 * erwartet — offene Anfragen und ungelesene Nachrichten. Aktivitaeten sind
 * einseitig ("jemand hat bewertet") und bekommen nur den stillen Punkt: Eine
 * Zahl, die beides zusammenzaehlt, wird schnell gross und damit bedeutungslos.
 */
export const getContactBadge = (contactRequests: ContactRequestFragment[] | undefined, unreadMessages = 0) : string | number | undefined => {
    const total = (contactRequests?.length ?? 0) + unreadMessages
    if (total === 0) return undefined

    return total <= 9 ? total : '9+'
}

const TabBar: React.FC = () => {
    const { t } = useTranslation()
    const [hideTabBar, setHideTabBar] = useState(true)
    const { routeInfo: { pathname } } = useIonRouter()
    const activeUrl = useTabContext()
    const [activeTab, setActiveTab] = useState<string>(activeUrl)

    const { newForeignActivities, pendingTalentTransferRequests, pendingContactRequests, unreadContactMessages } = useContextSelector(AppContext, state => state.state)

    useEffect(() => {
        if (activeTab !== activeUrl) {
            setActiveTab(() => activeUrl)
        }
    }, [activeTab, activeUrl])

    useLayoutEffect(() => {
        setHideTabBar(!showsTabBar(pathname))
    }, [pathname])

    useEffect(() => {
        if(Capacitor.isNativePlatform()) {
            Keyboard.addListener('keyboardWillShow', (info) => {
                setHideTabBar(true)
            })

            Keyboard.addListener('keyboardWillHide', () => {
                setHideTabBar(!showsTabBar(pathname))
            })
            return () => {
                Keyboard.removeAllListeners()
            }
        }
    }, [pathname]);

    return (
        <div
            className={clsx('tab-bar', { 'tab-bar--hidden': hideTabBar })}
        >
            <TabButton
                tab='home'
                icon={home}
                active={activeTab === 'home'}
                onClick={() => setActiveTab('home')}
            >
                {t('tabbar.home')}
            </TabButton>

            <TabButton
                badge={getTalentTransferBadge(pendingTalentTransferRequests)}
                tab='profile'
                icon={person}
                active={activeTab === 'profile'}
                onClick={() => setActiveTab('profile')}
            >
                {t('tabbar.profile')}
            </TabButton>

            <div
                className='tab-bar__search'
            >
                <TabButton
                    tab='marketplace/search'
                    icon={search}
                    color='secondary'
                    fill='solid'
                    active={activeTab === 'marketplace'}
                    round
                    onClick={() => setActiveTab('marketplace')}
                />
            </div>

            <TabButton
                badge={getContactBadge(pendingContactRequests, unreadContactMessages)}
                dot={(newForeignActivities?.length ?? 0) > 0}
                tab='inbox'
                icon={notifications}
                active={activeTab === 'inbox'}
                onClick={() => setActiveTab('inbox')}
            >
                {t('tabbar.inbox', 'Postfach')}
            </TabButton>

            <TabButton
                tab='playlists'
                icon={bookmarks}
                active={activeTab === 'playlists'}
                onClick={() => setActiveTab('playlists')}
            >
                {t('tabbar.playlists')}
            </TabButton>
        </div>
    )
}

export default TabBar
